import { useState, useEffect, useCallback } from 'react';
import { useSeason } from '@/context/SeasonContext';
import { hasSeasonEnded } from '@/utils/seasons/seasonHelpers';
import { supabase } from '@/utils/supabase/client';
import { checkTeamSeasonEnded, todayInViewerZone } from '@/utils/seasons/seasonValidation';

/**
 * Hook for client-side season validation
 * Provides reactive checks for season end status
 */
export function useSeasonValidation() {
  const { selectedSeason } = useSeason();
  const [seasonEnded, setSeasonEnded] = useState(false);
  const [checkingTeam, setCheckingTeam] = useState(false);

  // Check if selected season has ended
  useEffect(() => {
    if (!selectedSeason) {
      setSeasonEnded(false);
      return;
    }

    // Client-side check using helper function
    const ended = hasSeasonEnded(selectedSeason);
    setSeasonEnded(ended);
  }, [selectedSeason]);

  /**
   * Check if a team's season has ended
   * @param teamId - Team ID to check
   * @returns Promise with validation result
   */
  const checkTeamSeason = useCallback(async (teamId: string) => {
    if (!teamId) {
      return { hasEnded: false, season: null, teamSeasonId: null, error: 'No team ID provided' };
    }

    setCheckingTeam(true);
    try {
      const result = await checkTeamSeasonEnded(supabase, teamId, todayInViewerZone());
      return result;
    } catch (error) {
      return {
        hasEnded: false,
        season: null,
        teamSeasonId: null,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    } finally {
      setCheckingTeam(false);
    }
  }, []);

  return {
    seasonEnded,
    selectedSeason,
    checkTeamSeason,
    checkingTeam
  };
}


